import { z } from 'zod';
import type { CreativesListData } from '../creatives/data/api.js';
import {
  CREATIVE_SCORE_COMPONENT_ORDER,
  type CreativeScore,
  type CreativeScoreComponent,
  type CreativeScoreComponentName,
} from '../creatives/data/creativeScore.js';
import { creativeRowsOf, currencyOf, type ResultRow, SPEND_METRIC } from './resultRows.js';
import { asRecord, num, text } from './resultValues.js';

const CREATIVE_SCORES = 'creative_scores';
// The service's name for the score chip it puts in each row's metrics.
export const SCORE_METRIC = 'Score';
const VIEW_MODES = ['top_worst', 'distribution', 'benchmark', 'score_map'] as const;
const BUCKET_COUNT = 10;
const BUCKET_WIDTH = 100 / BUCKET_COUNT;

const COMPONENTS_SCHEMA = z.array(
  z.object({
    key: z.string(),
    label: z.string(),
    score: z.number().nullable(),
    weight: z.number(),
    insufficient_sample: z.boolean().optional(),
  }),
);
const SCORE_POINTS_SCHEMA = z.array(
  z.object({ creative_id: z.string(), score: z.number(), spend: z.number() }),
);

export type ScoreViewMode = (typeof VIEW_MODES)[number];

export interface ScoreComponentEntry {
  name: CreativeScoreComponentName;
  label: string;
  component: CreativeScoreComponent;
}

export interface ScorePoint {
  creativeId: string;
  score: number;
  spend: number;
}

export interface ScoreBucket {
  from: number;
  to: number;
  count: number;
}

function isComponentName(key: string): key is CreativeScoreComponentName {
  return (CREATIVE_SCORE_COMPONENT_ORDER as string[]).includes(key);
}

export function isCreativeScoresResult(result: unknown): boolean {
  return asRecord(result)?.['result_type'] === CREATIVE_SCORES;
}

// The service echoes its input params onto the result, view_mode among them.
export function viewModeOf(result: unknown): ScoreViewMode {
  const mode = text(asRecord(result)?.['view_mode']);
  return VIEW_MODES.find((value) => value === mode) ?? 'top_worst';
}

export function accountScoreOf(result: unknown): number | null {
  return num(asRecord(result)?.['account_score']);
}

// Every scored creative past the gate; `creatives` holds only the listed `limit`.
export function scorePointsOf(result: unknown): ScorePoint[] {
  const parsed = SCORE_POINTS_SCHEMA.safeParse(asRecord(result)?.['score_points']);
  if (!parsed.success) return [];
  return parsed.data.map((point) => ({
    creativeId: point.creative_id,
    score: point.score,
    spend: point.spend,
  }));
}

export function scoreOf(row: ResultRow): number | null {
  return num(row.values['score']);
}

export function spendAmountOf(row: ResultRow): number | null {
  return num(row.values['spend']);
}

// Components with a key this app does not know are left out: there is no accent to draw them in.
export function componentsOf(row: ResultRow): ScoreComponentEntry[] {
  const parsed = COMPONENTS_SCHEMA.safeParse(row.values['components']);
  if (!parsed.success) return [];
  return parsed.data.flatMap((entry) =>
    isComponentName(entry.key)
      ? [
          {
            name: entry.key,
            label: entry.label,
            component: {
              score: entry.score,
              weight: entry.weight,
              ...(entry.insufficient_sample === undefined
                ? {}
                : { insufficientSample: entry.insufficient_sample }),
            },
          },
        ]
      : [],
  );
}

export function scoreBuckets(points: ScorePoint[]): ScoreBucket[] {
  const buckets = Array.from({ length: BUCKET_COUNT }, (_, index) => ({
    from: index * BUCKET_WIDTH + 1,
    to: (index + 1) * BUCKET_WIDTH,
    count: 0,
  }));
  for (const { score } of points) {
    const index = Math.min(
      BUCKET_COUNT - 1,
      Math.max(0, Math.ceil(Math.round(score) / BUCKET_WIDTH) - 1),
    );
    (buckets[index] as ScoreBucket).count += 1;
  }
  return buckets;
}

export function leadingKpiLabelOf(rows: ResultRow[]): string {
  for (const row of rows) {
    const entry = componentsOf(row).find((component) => component.name === 'leadingKpi');
    if (entry !== undefined) return entry.label;
  }
  return '';
}

export function scoresCsvData(rows: ResultRow[]): CreativesListData {
  const byId = new Map(rows.map((row) => [row.creativeId, row]));
  const first = rows[0];
  return {
    currencySymbol: first === undefined ? '$' : currencyOf(first.values),
    columns: [{ name: SPEND_METRIC, label: SPEND_METRIC, format: 'compact_currency' }],
    totals: {},
    creatives: creativeRowsOf(rows).map((creative) => {
      const row = byId.get(creative.creativeId) as ResultRow;
      const creativeScore: CreativeScore = {
        score: scoreOf(row),
        components: Object.fromEntries(
          componentsOf(row).map((entry) => [entry.name, entry.component]),
        ),
      };
      return { ...creative, metrics: { [SPEND_METRIC]: spendAmountOf(row) }, creativeScore };
    }),
  };
}
